<template lang="pug">
    v-container.pb-md-5.width-100-percent.brown.lighten-5#message-dashboard-container(style="margin-top: 0px !important;" v-if="validDayDashboardSummary")
        v-row 
            v-col(cols='8')
                SummaryStats(
                    :current-day-dashboard-summary="currentDayDashboardSummary"
                )

            v-col(cols="4")
                Results(
                    :sheet-height="600"
                    :calls-waiting="callsWaiting"
                    :current-day-dashboard-summary="currentDayDashboardSummary"
                    :hourly-dashboard-summaries="hourlyDashboardSummaries"
                )
        v-row
            v-col(cols="12")
                AgentIndex(
                    :agents="renderAgents"
                    :set-supervisor-role="setSupervisorRole"
                    :set-as-call-without-customer="setAsCallWithoutCustomer"
                    :set-current-call="setCurrentCall"
                    :set-current-campaign-customer="setCurrentCampaignCustomer"
                    :set-current-web-rtc-participant="setCurrentWebRtcParticipant"
                    :set-current-web-rtc-session="setCurrentWebRtcSession"
                )
            v-col(cols="12")
                Organizations(
                    :organization-users="organizationUsers"
                )
    .text-center(v-else)
        v-progress-circular(
            :size='100'
            :width='10'
            color='var(--volie-blue)'
            indeterminate
            style="margin-top: 75px; margin-bottom: 75px;"
        )

</template>

<script lang="ts">
    import Vue from 'vue';
    import VolieState  from "@/store";

    // Load components
    import AgentIndex from '@/components/dashboards/live/calls/agent_index.vue';
    import Organizations from '@/components/dashboards/live/calls/organizations.vue';
    import SummaryStats from '@/components/dashboards/live/calls/summary_stats.vue';
    import Results from '@/components/dashboards/live/overview/results.vue';

    // Data
    import { GetCampaignCustomerActives } from "@/data";

    export default Vue.extend({
        data() {
            return {
                assignedCampaignCustomerActives: [],
                bus: VolieState.websocketClient().eventBus(),
                isLoading: false,
                renderAgents: [],
                resultsSheetHeight: null,
            }
        },
        methods: {
            buildRenderAgents() {

                // If we don't have valid agents, abort
                if (!this.validAgents) {
                    this.renderAgents = [];
                    return;
                }

                // Init
                const tmpAgents = this.agents.slice();
                if (!(tmpAgents?.length > 0)) {
                    this.renderAgents = [];
                    return;
                }

                // Loop through tmp agents and filter based on status
                const renderAgents = [];
                for (let i = 0; i < tmpAgents.length; i++) {

                    const agent = { ...tmpAgents[i] };
                    if (!agent || !(agent.account_user_id > 0) || !!agent.supervisor_role) {
                        continue;
                    }

                    // Switch over status type
                    switch(agent.agent_status_name) {
                    case "Outgoing Call": // outbound
                        renderAgents.push(agent);
                        break;
                    case "Incoming Call": // inbound
                        renderAgents.push(agent);
                        break;
                    }
                }

                // If filtered agents is empty, abort
                if (!(renderAgents.length > 0)) {
                    this.renderAgents = [];
                    return;
                }

                // Fill in assigned cc actives
                if (this.assignedCampaignCustomerActives?.length > 0) {

                    // Sort first
                    const ccActives = this.sortCampaignCustomerActives(this.assignedCampaignCustomerActives.slice(0));
                    if (ccActives?.length > 0) {
                        // Loop through and append to agents
                        for (let i = 0; i < ccActives.length; i++) {

                            // Clone the cc active AND its nested conversation_group so the clock can mutate
                            // unresponded_to_time_in_seconds without touching the watched source and retriggering this rebuild
                            const ccActive = { ...ccActives[i] };
                            if (ccActive.conversation_group) {
                                ccActive.conversation_group = { ...ccActive.conversation_group };
                            }
                            if (!(ccActive?.campaign_customer_active_id > 0 && ccActive.assigned_to > 0 && ccActive.account_id > 0)) {
                                continue;
                            }

                            // Loop through render agents and find agent
                            for (let j = 0; j < renderAgents.length; j++) {

                                // Init
                                const agent = renderAgents[j];

                                // Check if user's match
                                if (!(agent.user_id > 0 && agent.user_id === ccActive.assigned_to && agent.account_id > 0 && agent.account_id === ccActive.account_id)) {
                                    continue;
                                }

                                // Own the array so we don't mutate the shared source agent
                                if (!(agent.assigned_campaign_customer_actives?.length > 0)) {
                                    agent.assigned_campaign_customer_actives = [];
                                } else {
                                    agent.assigned_campaign_customer_actives = agent.assigned_campaign_customer_actives.slice();
                                }

                                // Add to agent's stack
                                agent.assigned_campaign_customer_actives.push(ccActive);
                                break;
                            }
                        }
                    }
                }

                // Compute live timers up front so rows never render without a duration (avoids 00:00 + color flash)
                this.applyClockValues(renderAgents);

                // Sort by longest duration. Relative order only changes when membership changes, so we sort here
                // (on rebuild) rather than every clock tick.
                this.renderAgents = this.sortAgents(renderAgents);
            },
            getCampaignCustomerActives() {
                this.assignedCampaignCustomerActives = [];
                const options = {
                    purposes: ["return_call", "default"],
                    statuses: ["assigned", "in_progress"],
                }

                this.isLoading = true;
                GetCampaignCustomerActives(options).then((response) => {
                    if (response.Data?.campaign_customer_actives?.length > 0) {
                        this.assignedCampaignCustomerActives = response.Data?.campaign_customer_actives;
                    } else {
                        this.assignedCampaignCustomerActives = [];
                    }

                    this.isLoading = false;
                });
            },
            handleWindowResize() {

                // Declare constants
                const tabsHeight = 88;
                const topHeader = 64;
                const bannerHeight = 57;
                const sheetPx = 8;
                const bottomPx = 12;

                // Calcuate
                this.resultsSheetHeight = this.$refs.mainOverviewRowRef?.clientHeight - topHeader - tabsHeight - bannerHeight - sheetPx - bottomPx;
            },
            onCampaignCustomerActiveStatusChange(response) {

                // Verify legit cc
                const campaignCustomer = response?.Data;
                if (!(campaignCustomer?.campaign_customer_active_id > 0) || !(campaignCustomer?.account_id > 0) || !(campaignCustomer?.purpose?.length > 0) || !(campaignCustomer?.status?.length > 0)) {
                    return;
                }

                // Verify purpose is return message
                if (campaignCustomer.purpose !== "default" && campaignCustomer.purpose !== "return_call") {
                    return;
                }

                // Find in slice
                const idx = this.assignedCampaignCustomerActives.findIndex(i => i.campaign_customer_active_id === campaignCustomer.campaign_customer_active_id);
                if (idx < 0) {
                    return;
                }

                // Init
                const assigned = this.assignedCampaignCustomerActives[idx];
                if (!assigned) {
                    return;
                }

                // If record is still assigned, update status on record. Otherwise remove from slice (completed, requeued, etc)
                if (campaignCustomer.status === "assigned" || campaignCustomer.status === "in_progress") {
                    assigned.status = campaignCustomer.status;
                } else {
                    this.assignedCampaignCustomerActives.splice(idx, 1);
                }
            },
            onNewAssignedCampaignCustomer(response) {

                // Verify legit cc
                const campaignCustomer = response?.Data;
                if (!(campaignCustomer?.campaign_customer_active_id > 0) || !(campaignCustomer?.account_id > 0) || !(campaignCustomer?.assigned_to > 0) || !(campaignCustomer?.purpose?.length > 0)) {
                    return;
                }

                // Verify purpose is return message
                if (campaignCustomer.purpose !== "default" && campaignCustomer.purpose !== "return_call") {
                    return;
                }

                // Determine if record already is in array
                const idx = this.assignedCampaignCustomerActives.findIndex(i => i.campaign_customer_active_id === campaignCustomer.campaign_customer_active_id);
                if (idx >= 0) {
                    return;
                }

                // Push onto array
                this.assignedCampaignCustomerActives.push(campaignCustomer);
            },
            applyClockValues(agents) {

                // Verify render agents
                if (!(agents?.length > 0)) {
                    return agents;
                }

                // Init
                const currentTime = new Date();

                // Loop through agents and set time in seconds
                for (let i = 0; i < agents.length; i++) {

                    // Init agent
                    const agent = agents[i];
                    if (!agent || !agent.agent_created_at) {
                        continue;
                    }

                    // Init agent time
                    const agentTime = new Date(agent.agent_created_at);

                    // Calculate clock
                    agent.time_in_seconds = (Math.floor(currentTime.getTime() - agentTime.getTime()) / 1000);

                    if (!(agent?.assigned_campaign_customer_actives?.length > 0)) {
                        continue;
                    }

                    // Loop through records and set unresponded to time
                    for (let j = 0; j < agent.assigned_campaign_customer_actives.length; j++) {

                        // Init
                        const ccActive = agent.assigned_campaign_customer_actives[j];
                        if (!ccActive || !ccActive.conversation_group) {
                            continue;
                        }

                        // Verify there is an unresponded message
                        if (!!ccActive.conversation_group.unresponded_to_incoming_message_at) {
                            const unrespondedToTime = new Date(ccActive.conversation_group.unresponded_to_incoming_message_at);
                            ccActive.conversation_group.unresponded_to_time_in_seconds = (Math.floor(currentTime.getTime() - unrespondedToTime.getTime()) / 1000);
                        } else {
                            ccActive.conversation_group.unresponded_to_time_in_seconds = null;
                        }
                    }
                }

                return agents;
            },
            setClockValues() {

                // Tick the live timers in place. We intentionally do NOT re-sort here: durations increment
                // uniformly so relative order never changes between ticks (re-sorting caused visible reordering).
                this.applyClockValues(this.renderAgents);
            },
            sortAgents(agents) {
                if (!(!!agents && agents.length > 0)) {
                    return [];
                }

                return agents.sort(function(x, y) {
                    const diff = (y.time_in_seconds || 0) - (x.time_in_seconds || 0);
                    if (diff !== 0) {
                        return diff;
                    }

                    // Stable tie-breaker so equal durations keep a deterministic order
                    return (x.call_id || 0) - (y.call_id || 0);
                });
            },
            sortCampaignCustomerActives(ccActives) {
                if (!(!!ccActives && ccActives.length > 0)) {
                    return [];
                }
                return ccActives.sort(function(x, y) {
            
                    return y?.campaign_customer_active_id - x?.campaign_customer_active_id;
                });
            },
            startClock() {
                this.clockInterval = setInterval(this.setClockValues, 1000);
            },
            stopClock() {
                if (!this.clockInterval) {
                    return;
                }

                clearInterval(this.clockInterval);
            },
        },
        computed: {
            validAgents() {
                return this.agents?.length > 0;
            },
            validDayDashboardSummary() {
                return this.currentDayDashboardSummary?.id > 0;
            },
        },
        components: {
            AgentIndex,
            Organizations,
            Results,
            SummaryStats,
        },
        props: {
            agents: Array,
            callsWaiting: Array,
            currentBaseDashboardSummary: Object,
            currentDayDashboardSummary: Object,
            hourlyDashboardSummaries: Array,
            organizationUsers: Array,
            setSupervisorRole: Function,
            setAsCallWithoutCustomer: Function,
            setCurrentCall: Function,
            setCurrentCampaignCustomer: Function,
            setCurrentWebRtcParticipant: Function,
            setCurrentWebRtcSession: Function,
        },
        watch: {
            agents: {
                deep: true, // This will let Vue know to look inside the array
                handler() {
                    
                    // Then on next tick, rerender => needs to be this way so the clocks all are re-created at the same time
                    Vue.nextTick(() => { this.buildRenderAgents(); });
                },
            },
            assignedCampaignCustomerActives: {
                deep: true, // This will let Vue know to look inside the array
                handler() {
                    
                    // Then on next tick, rerender => needs to be this way so the clocks all are re-created at the same time
                    Vue.nextTick(() => { this.buildRenderAgents(); });
                },
            },
        },
        destroyed() {
            this.stopClock();
            this.bus.unsubscribe({ type: "broadcast" , channel: { ID: null, Type: "campaignCustomerActiveStatusChange" }}, this.onCampaignCustomerActiveStatusChange);
            this.bus.unsubscribe({ type: "broadcast" , channel: { ID: null, Type: "newAssignedCampaignCustomer" }}, this.onNewAssignedCampaignCustomer);
        },
        created() {
            this.bus.subscribe({ type: "broadcast" , channel: { ID: null, Type: "campaignCustomerActiveStatusChange" }}, this.onCampaignCustomerActiveStatusChange);
            this.bus.subscribe({ type: "broadcast" , channel: { ID: null, Type: "newAssignedCampaignCustomer" }}, this.onNewAssignedCampaignCustomer);
            this.startClock();
            this.getCampaignCustomerActives();
            this.buildRenderAgents();
        },
    });
</script>

<style lang="scss" scoped>
</style>